import { cn } from '../utils/cn';
import Spinner from './Spinner';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
  type?: 'submit' | 'button';
  variant?: 'filled' | 'outlined';
  color?: 'primary' | 'secondary';
  loading?: boolean;
  hideTextOnLoading?: boolean;
  className?: string;
  onClick?: () => void;
};

const baseClasses =
  'flex w-full justify-center items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-secondary disabled:cursor-not-allowed transition-all duration-200 ease-in-out';

const filledClasses = {
  primary: 'bg-primary text-lighter shadow-sm hover:bg-primary-dark',
  secondary: 'bg-secondary text-lighter shadow-sm hover:bg-dark',
};

const outlinedClasses = {
  primary: 'border border-primary text-primary bg-transparent hover:bg-primary hover:text-lighter',
  secondary: 'border border-secondary bg-transparent hover:bg-secondary text-light',
};

const Button = ({
  children,
  type = 'submit',
  variant = 'filled',
  color = 'primary',
  loading = false,
  hideTextOnLoading = false,
  className,
  onClick,
  ...props
}: ButtonProps) => {
  const variantClasses = variant === 'filled' ? filledClasses[color] : outlinedClasses[color];
  const isDisabled = loading || props.disabled;

  return (
    <button
      type={type}
      className={cn(baseClasses, variantClasses, loading && hideTextOnLoading ? 'relative' : '', className)}
      onClick={onClick}
      disabled={isDisabled}
      {...props}
    >
      {loading && hideTextOnLoading ? (
        <>
          <span className="invisible">{children}</span>
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner size="sm" className="text-white/20 fill-white" />
          </span>
        </>
      ) : (
        <>
          {loading ? <Spinner size="sm" className="text-white/20 fill-white" /> : null}
          {children}
        </>
      )}
    </button>
  );
};

export default Button;
